import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import Banner from "@adsk/alloy-react-banner";
import { ArrowRightIcon } from "@adsk/alloy-react-icon";
import { ModalConfirmation } from "@adsk/alloy-react-modal";
import Accordion, { AccordionItem } from "@adsk/alloy-react-accordion";
import { ReferencePickerField } from "../../input-field-components/ReferencePickerField";
import { EditableDateField } from "@adsk/acc-issues-common/components/input/DateField";
import { DetailPanelTypeField } from "./detail-panel-fields/DetailPanelTypeField";
import { StatusField } from "./detail-panel-fields/StatusField";
import { RootCauseField } from "./detail-panel-fields/RootCauseField";
import { DescriptionField } from "./detail-panel-fields/DescriptionField";
import { TitleField } from "./detail-panel-fields/TitleField";
import { LocationDetailsField } from "./detail-panel-fields/LocationDetailsField";
import { AssignedToField } from "./detail-panel-fields/AssignedToField";
import { WatcherField } from "./detail-panel-fields/WatcherField";
import { PlacementField } from "./detail-panel-fields/PlacementField";
import { usePlacementFieldConfig } from "../issueDetailPanelConfigContext";
import { LocationPickerField } from "../../input-field-components/LocationPickerField";
import { GeoLocationPickerField } from "../../input-field-components/GeoLocationPickerField";
import { AttachmentsField } from "../../input-field-components/AttachmentsField";
import { isAttributeEditable } from "../../utils/data/entityPermissions";
import { useCustomFieldIdsWithMappingsForType } from "../../utils/hooks/issueCustomFieldMappingHooks";
import {
  FieldMetadataForTypes as FieldMetadata,
  GpsCoordinates,
  Issue,
  IssueStandardFieldIds,
  IssueStatus,
  IssueType,
  PermittedAction,
} from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import {
  useDispatch,
  useSelector,
} from "@adsk/acc-issues-core/state/redux/hooks";
import { mergeIssuePendingUpdates } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import {
  IssueChange,
  IssueChangeFn,
} from "./detail-panel-fields/typesForFields";
import { getIssueValueFromFieldMetadata } from "../../utils/data/issueStandardFields";
import {
  getAreAllRequiredFieldsFilled,
  getIsIssueValueEmpty,
} from "../../utils/data/issueRequiredFields";
import { useGetSanitizedFieldOrder } from "../../utils/data/issueFieldOrdering";
import { IssueEditFormCustomField } from "./detail-panel-fields/IssueEditFormCustomField";
import { DelayRender } from "../../utils/components/DelayRender";
import { StatusDisplay } from "@adsk/acc-issues-common/components/display/StatusDisplay";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import {
  fetchRelationshipsForDeletedIssueAction,
  getRelationships,
} from "@adsk/acc-issues-core/state/redux/ducks/relationships";
import { useHasPermissionToRestoreIssue } from "./hooks/IssueDeleteHooks/useHasPermissionToRestoreIssues";
import { ThumbnailField } from "./detail-panel-fields/ThumbnailField";
import { is3dIssue } from "../../utils/data/issueLinkedDocumentsUtils";
import { trackIssueThumbnailOpen } from "../../utils/analytics/issueActionsTracking";
import { useWatcherPropName } from "../../utils/hooks/watchers/useWatcherPropName";
import { useGetFilledAndUnpopulatedFields } from "../../hooks/useGetFilledAndUnpopulatedFields";
import { useShouldExpandEmptyFieldsAccordion } from "../../hooks/useShouldExpandEmptyFieldsAccordion";

import { useIssuePlacementType } from "@adsk/acc-issues-core/utils/hooks/useIssuePlacementType";

interface IssueEditFormProps {
  issue: Issue;
  handleChange: IssueChangeFn;
  onUpdateFailed: () => void;
  errors: { [key: string]: string };
  withPlacementLink: boolean;
  viewOnly?: boolean;
  panelOpenedAfterIssueCreation?: boolean;
}

const getDidCompletePreviouslyEmptyRequiredField = ({
  issue,
  issueChange,
  fieldsMetadata,
  watcherProp,
}: {
  issue: Issue;
  issueChange: IssueChange;
  fieldsMetadata: FieldMetadata[];
  watcherProp: "watchers" | "watcherObjects";
}) => {
  const requiredFields = fieldsMetadata.filter(({ required }) => !!required);

  // in practice we only change 1 field at a time, but if multiple change, we check if at least 1 of them was required
  return requiredFields.some(field => {
    const newValue = getIssueValueFromFieldMetadata({
      field,
      watcherProp,
      issue: issueChange as Issue,
    });
    const oldValue = getIssueValueFromFieldMetadata({
      field,
      issue,
      watcherProp,
    });

    return !getIsIssueValueEmpty(newValue) && getIsIssueValueEmpty(oldValue);
  });
};

export const IssueEditForm = React.memo(
  ({
    issue,
    handleChange,
    onUpdateFailed,
    errors,
    withPlacementLink,
    viewOnly = false,
    panelOpenedAfterIssueCreation = false,
  }: IssueEditFormProps) => {
    const typeEntities = useSelector(getIssueTypeEntitiesMap);
    const currentType: IssueType = typeEntities?.[issue.issueTypeId!];

    const placementFieldConfig = usePlacementFieldConfig();
    const placementOptions = viewOnly
      ? { navigable: false, editable: false, unlinkable: false }
      : withPlacementLink
      ? { navigable: true, editable: false, unlinkable: true }
      : placementFieldConfig;

    const { mappingsByCustomFieldId, customFieldIds } =
      useCustomFieldIdsWithMappingsForType(issue.issueTypeId!);

    const { value: shouldHideEmptyRolesAndCompanies } = useFeatureFlag(
      FeatureFlag.HideEmptyRolesAndCompanies
    );

    const { value: shouldShowAttachments } = useFeatureFlag(
      FeatureFlag.ShowAttachments
    );

    const getIssuePlacementType = useIssuePlacementType();
    const placementType = getIssuePlacementType(issue);

    const handleThumbnailClick = useCallback(() => {
      trackIssueThumbnailOpen(placementType);
    }, [placementType]);

    const issueFields = useGetSanitizedFieldOrder({
      fieldList: currentType?.fieldsMetadata || [],
      customFieldIdsForType: customFieldIds,
      shouldShowAttachments,
    });

    const { value: shouldAllowThumbnailFor2DIssue } = useFeatureFlag(
      FeatureFlag.IssueThumbnailOf2dSnapshots
    );

    const { populatedFields, unpopulatedFields } =
      useGetFilledAndUnpopulatedFields(issue, issueFields);

    const shouldShowCollapsibleSection =
      !panelOpenedAfterIssueCreation &&
      unpopulatedFields?.length > 2 &&
      issue.status !== IssueStatus.Draft;

    const [isExpanded, setIsExpanded] = useShouldExpandEmptyFieldsAccordion();

    const [isShowingStatusChangeModal, setIsShowingStatusChangeModal] =
      useState(false);

    const isDeletedSpaceActive = useContext(DeletedIssuesSpaceContext);
    const dispatch = useDispatch();
    const watcherProp = useWatcherPropName();

    useEffect(() => {
      if (!isDeletedSpaceActive) {
        return;
      }
      dispatch(
        fetchRelationshipsForDeletedIssueAction({
          issueId: issue.id,
          onlyDeleted: isDeletedSpaceActive,
          deletedAfter: issue.deletedAt!,
        })
      );
    }, [dispatch, isDeletedSpaceActive, issue.deletedAt, issue.id]);

    const relationshipsData = useSelector(getRelationships);
    const maybeShowStatusChangeModal = useCallback(
      (issueChange: IssueChange) => {
        const newIssue = mergeIssuePendingUpdates(
          issue,
          issueChange as Partial<Issue>
        );

        if (newIssue.status === IssueStatus.Draft) {
          const areAllRequiredFieldsFilled = getAreAllRequiredFieldsFilled({
            watcherProp,
            fieldsMetadata: issueFields,
            issue: newIssue,
          });

          const didCompletePreviouslyEmptyRequiredField =
            getDidCompletePreviouslyEmptyRequiredField({
              issue,
              issueChange,
              watcherProp,
              fieldsMetadata: issueFields,
            });

          if (
            areAllRequiredFieldsFilled &&
            didCompletePreviouslyEmptyRequiredField
          ) {
            setIsShowingStatusChangeModal(true);
          }
        }
      },
      [issue, issueFields]
    );

    const handleChangeAndMaybeShowStatusChangeModal: IssueChangeFn =
      useCallback(
        issueChange => {
          handleChange(issueChange);
          maybeShowStatusChangeModal(issueChange);
        },
        [handleChange, maybeShowStatusChangeModal]
      );

    const isStatusFieldDisabledDueToRequiredFields =
      !getAreAllRequiredFieldsFilled({
        watcherProp,
        fieldsMetadata: issueFields,
        issue,
      }) && issue.status === IssueStatus.Draft;

    const { T } = useTranslations();
    const { showNotification } = useNotifications();

    const { value: shouldShowRestoreDeletedIssueButton } = useFeatureFlag(
      FeatureFlag.RestoreDeletedIssues
    );

    const displayThumbnailField = useMemo(() => {
      return is3dIssue(issue) || shouldAllowThumbnailFor2DIssue;
    }, [shouldAllowThumbnailFor2DIssue, issue]);

    const hasPermissionToRestoreIssue = useHasPermissionToRestoreIssue(issue);
    const regularMessage = T.translate(
      "deletedIssues.issuesDetailBannerMessage"
    );
    const contactAdminToRestore = T.translate(
      "deletedIssues.issuesDetailBannerMessageNoPermissions"
    );
    const messageOfRestoringIssue = shouldShowRestoreDeletedIssueButton
      ? hasPermissionToRestoreIssue
        ? regularMessage
        : contactAdminToRestore
      : regularMessage;

    const renderFields = (fields: FieldMetadata[]) => {
      return fields.map((field: FieldMetadata) => {
        const fieldId = field.id;
        const isFieldRequired = field.required;

        if (!field.visible) {
          // do not show fields that should not be visible!
          return null;
        }
        if (fieldId === IssueStandardFieldIds.Description) {
          return (
            <DescriptionField
              key="descriptionField"
              T={T}
              handleChange={handleChangeAndMaybeShowStatusChangeModal}
              issue={issue}
              required={isFieldRequired}
              viewOnly={viewOnly}
            />
          );
        } else if (fieldId === IssueStandardFieldIds.Assignee) {
          return (
            <AssignedToField
              key={"assignedToField"}
              issue={issue}
              onChange={handleChangeAndMaybeShowStatusChangeModal}
              required={isFieldRequired}
              viewOnly={viewOnly}
              shouldIncludeEmpty={!shouldHideEmptyRolesAndCompanies}
            />
          );
        } else if (fieldId === IssueStandardFieldIds.Watchers) {
          return (
            <WatcherField
              key="watcherField"
              issue={issue}
              onChange={handleChangeAndMaybeShowStatusChangeModal}
              required={isFieldRequired}
              viewOnly={viewOnly}
            />
          );
        } else if (fieldId === IssueStandardFieldIds.Location) {
          return (
            <LocationPickerField
              key="locationField"
              issue={issue}
              onChange={handleChangeAndMaybeShowStatusChangeModal}
              required={isFieldRequired}
              viewOnly={viewOnly}
            />
          );
        } else if (fieldId === IssueStandardFieldIds.LocationDetails) {
          return (
            <LocationDetailsField
              key="locationDetailsField"
              T={T}
              handleChange={handleChangeAndMaybeShowStatusChangeModal}
              issue={issue}
              required={isFieldRequired}
              viewOnly={viewOnly}
            />
          );
        } else if (fieldId === IssueStandardFieldIds.GpsCoordinates) {
          return (
            <GeoLocationPickerField
              key="gpsCoordinatesField"
              issue={issue}
              onChange={(gpsCoordinates: GpsCoordinates) => {
                handleChange({ gpsCoordinates });
              }}
              disabled={
                viewOnly || !isAttributeEditable(issue, "gpsCoordinates")
              }
            />
          );
        } else if (fieldId === IssueStandardFieldIds.DueDate) {
          return (
            <div key="dueDateField" data-testid="due-date-field">
              <EditableDateField
                fieldKey="dueDate"
                label={T.translate("issueAttributes.dueDate")}
                date={issue.dueDate}
                disabled={viewOnly || !isAttributeEditable(issue, "dueDate")}
                viewOnly={viewOnly}
                onChange={newDate => {
                  handleChangeAndMaybeShowStatusChangeModal({
                    dueDate: newDate,
                  });
                }}
                defaultIsOpen={true}
                required={isFieldRequired}
              />
            </div>
          );
        } else if (fieldId === IssueStandardFieldIds.StartDate) {
          return (
            <div key="startDateField" data-testid="start-date-field">
              <EditableDateField
                fieldKey="startDate"
                label={T.translate("issueAttributes.startDate")}
                date={issue.startDate}
                disabled={viewOnly || !isAttributeEditable(issue, "startDate")}
                viewOnly={viewOnly}
                onChange={newDate => {
                  handleChangeAndMaybeShowStatusChangeModal({
                    startDate: newDate,
                  });
                }}
                defaultIsOpen={true}
                required={isFieldRequired}
              />
            </div>
          );
        } else if (fieldId === IssueStandardFieldIds.Placement) {
          return (
            <PlacementField
              key="placementField"
              error={errors.placement}
              issue={issue}
              onChange={handleChange}
              issueType={currentType}
              navigable={placementOptions.navigable}
              editable={placementOptions.editable}
              unlinkable={placementOptions.unlinkable}
            />
          );
        } else if (fieldId === IssueStandardFieldIds.RootCauseWithCategory) {
          return (
            <RootCauseField
              key={"rootCauseField"}
              issue={issue}
              onChange={handleChangeAndMaybeShowStatusChangeModal}
              required={isFieldRequired}
              viewOnly={viewOnly}
            />
          );
        } else if (
          fieldId === IssueStandardFieldIds.Attachments &&
          shouldShowAttachments
        ) {
          return (
            <AttachmentsField
              key="attachmentsField"
              issue={issue}
              isDeletedIssue={viewOnly}
            />
          );
        } else if (fieldId === IssueStandardFieldIds.References) {
          return (
            <DelayRender delay={400} key="referencePickerField">
              <ReferencePickerField
                issue={issue}
                sourceEntityId={issue.id}
                canAddReferences={
                  !viewOnly &&
                  issue.permittedActions.includes(PermittedAction.AddAttachment)
                }
                canRemoveReferences={
                  !viewOnly &&
                  issue.permittedActions.includes(
                    PermittedAction.RemoveAttachment
                  )
                }
                relationshipEntitiesList={
                  isDeletedSpaceActive ? relationshipsData : undefined
                }
              />
            </DelayRender>
          );
        } else if (
          field.type === "custom" &&
          mappingsByCustomFieldId[field.id]
        ) {
          const mapping = mappingsByCustomFieldId[field.id];
          return (
            <IssueEditFormCustomField
              {...mapping}
              issue={issue}
              onUpdate={maybeShowStatusChangeModal}
              onUpdateFailed={onUpdateFailed}
              key={mapping.attributeDefinitionId}
              required={isFieldRequired}
              viewOnly={viewOnly}
            />
          );
        }
      });
    };

    const wrapInAccordion = (fields: (JSX.Element | null | undefined)[]) => {
      return (
        <Accordion initialExpanded={[]} style={{ paddingTop: "1rem" }}>
          <AccordionItem
            title={T.translate("issueEditForm.collapsibleSection.title", {
              quantity: fields.length,
            })}
            headerStyle={{
              width: "calc(100% + 32px)",
              left: -16,
              fontFamily: "inherit",
              fontSize: "inherit",
            }}
            expanded={isExpanded}
            onExpand={() => setIsExpanded(!isExpanded)}
            containerProps={{
              style: { overflow: isExpanded ? "visible" : "hidden" },
            }}
            data-testid="empty-fields-accordion-toggle"
          >
            {fields}
          </AccordionItem>
        </Accordion>
      );
    };

    return (
      <div>
        {isStatusFieldDisabledDueToRequiredFields && (
          <Banner
            inline
            variant={Banner.VARIANTS.WARNING}
            message={T.translate("issueEditForm.requiredFields.bannerMessage")}
            style={{ marginTop: 16, paddingRight: 16 }}
          />
        )}
        {viewOnly && (
          <Banner
            inline
            variant={Banner.VARIANTS.PRIMARY}
            message={messageOfRestoringIssue}
            style={{ marginTop: 16, paddingRight: 16 }}
          />
        )}
        {displayThumbnailField && (
          <ThumbnailField
            issue={issue}
            onThumbnailClick={handleThumbnailClick}
          />
        )}
        {/** title, status, and type are never hidden or reordered, so we can render them statically */}
        <TitleField
          viewOnly={viewOnly}
          T={T}
          handleChange={handleChange}
          issue={issue}
          shouldSelectInputContent={panelOpenedAfterIssueCreation}
        />
        <StatusField
          data-testid="status-field"
          viewOnly={viewOnly}
          issue={issue}
          onChange={handleChange}
          isDisabledDueToRequiredFields={
            isStatusFieldDisabledDueToRequiredFields
          }
        />
        <DetailPanelTypeField
          // the conditional tooltip is included in the typeField already
          key={0}
          issue={issue}
          onChange={handleChange}
          viewOnly={viewOnly}
        />
        {shouldShowCollapsibleSection ? (
          <>
            {renderFields(populatedFields)}
            {wrapInAccordion(renderFields(unpopulatedFields))}
          </>
        ) : (
          <>{renderFields(issueFields)}</>
        )}
        <ModalConfirmation
          style={{}}
          show={isShowingStatusChangeModal}
          onConfirm={() => {
            handleChange(
              { status: IssueStatus.Open },
              {
                onSuccess: () => {
                  showNotification({
                    status: NOTIFICATION_STATUSES.SUCCESS,
                    message: T.translate(
                      "issueEditForm.requiredFields.updateStatusModal.successMessage"
                    ),
                  });
                },
              }
            );
            setIsShowingStatusChangeModal(false);
          }}
          onClose={() => setIsShowingStatusChangeModal(false)}
          title={T.translate(
            "issueEditForm.requiredFields.updateStatusModal.title"
          )}
          message={
            <div>
              {T.translate(
                "issueEditForm.requiredFields.updateStatusModal.message"
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "35%",
                }}
              >
                <StatusDisplay status={IssueStatus.Draft} />
                <ArrowRightIcon />
                <StatusDisplay status={IssueStatus.Open} />
              </div>
            </div>
          }
          confirmationButtonLabel={
            T.translate(
              "issueEditForm.requiredFields.updateStatusModal.setToOpen"
            ) as string
          }
          cancelButtonLabel={
            T.translate(
              "issueEditForm.requiredFields.updateStatusModal.keepAsDraft"
            ) as string
          }
          isConfirmationButtonAlert={false}
        />
      </div>
    );
  }
);
